export const serviceAreasIconTokens = [
    'arrowRight',
    'badgeCheck',
    'calendar',
    'checkCircle',
    'clock',
    'mapPin',
    'navigation',
    'search',
    'shieldCheck',
    'users',
    'zap',
] as const;

export type ServiceAreasIconToken = (typeof serviceAreasIconTokens)[number];

export const serviceAreasTokens = {
    colors: {
        background: '#ffffff',
        surface: '#ffffff',
        surfaceSoft: '#f8fbff',
        text: '#07142f',
        textSoft: '#2d3b5c',
        textMuted: '#64748b',
        accent: '#1268f3',
        accentDark: '#0b4ec0',
        accentSoft: 'rgba(18, 104, 243, 0.13)',
        accentWash: 'rgba(18, 104, 243, 0.08)',
        accentContrast: '#ffffff',
        action: '#ff7900',
        border: 'rgba(7, 20, 47, 0.1)',
        borderStrong: 'rgba(7, 20, 47, 0.16)',
        mapOverlay: 'rgba(18, 104, 243, 0.14)',
        mapStroke: '#1268f3',
        shadow: '0 20px 54px rgba(7, 20, 47, 0.1)',
        softShadow: '0 12px 32px rgba(7, 20, 47, 0.08)',
    },
    radius: {
        shell: 8,
        panel: 8,
        media: 8,
        button: 8,
        icon: 999,
    },
    spacing: {
        sectionY: 36,
        sectionX: 20,
        shellPadding: 22,
        panelPadding: 32,
        panelPaddingMobile: 18,
        gridGap: 26,
        featureGap: 22,
    },
    sizes: {
        maxWidth: 1180,
        contentMaxWidth: 540,
        mapMinHeight: 356,
        mapMobileMinHeight: 240,
        icon: 34,
    },
    typography: {
        fontFamily:
            'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
        eyebrow: {
            fontSize: 13,
            mobileFontSize: 12,
            lineHeight: 1.15,
            fontWeight: 850,
            letterSpacing: 0,
            textTransform: 'uppercase' as const,
        },
        title: {
            fontSize: 36,
            tabletFontSize: 32,
            mobileFontSize: 28,
            lineHeight: 1.12,
            fontWeight: 850,
            letterSpacing: 0,
        },
        description: {
            fontSize: 16,
            mobileFontSize: 15,
            lineHeight: 1.58,
            fontWeight: 500,
            letterSpacing: 0,
        },
        featureTitle: {
            fontSize: 13,
            mobileFontSize: 12,
            lineHeight: 1.25,
            fontWeight: 800,
            letterSpacing: 0,
        },
        areaTitle: {
            fontSize: 18,
            mobileFontSize: 15,
            lineHeight: 1.3,
            fontWeight: 850,
            letterSpacing: 0,
        },
        areaLink: {
            fontSize: 14,
            mobileFontSize: 13,
            lineHeight: 1.35,
            fontWeight: 650,
            letterSpacing: 0,
        },
    },
    defaults: {
        eyebrow: 'Service areas',
        title: 'Split system servicing across Sydney',
        description:
            'We provide professional split system servicing throughout Sydney and surrounding areas. Our local technicians ensure fast response times and reliable service, wherever you are.',
        features: [
            {
                title: 'Local Technicians',
                icon: 'users' as ServiceAreasIconToken,
            },
            {
                title: 'Fast Response',
                icon: 'zap' as ServiceAreasIconToken,
            },
            {
                title: 'Upfront Pricing',
                icon: 'shieldCheck' as ServiceAreasIconToken,
            },
        ],
        imageBadge: {
            title: 'We service all areas',
            description: 'within the Sydney region',
            icon: 'checkCircle' as ServiceAreasIconToken,
        },
        areasTitle: 'Popular areas we service',
        viewAllAreasLabel: 'View all areas',
        areas: [
            {
                label: 'Split system servicing in Menai',
                shortLabel: 'Menai',
                href: '#menai',
            },
            {
                label: 'Split system servicing in Gymea',
                shortLabel: 'Gymea',
                href: '#gymea',
            },
            {
                label: 'Split system servicing in Engadine',
                shortLabel: 'Engadine',
                href: '#engadine',
            },
            {
                label: 'Split system servicing in Cronulla',
                shortLabel: 'Cronulla',
                href: '#cronulla',
            },
            {
                label: 'Split system servicing in Sutherland',
                shortLabel: 'Sutherland',
                href: '#sutherland',
            },
            {
                label: 'Split system servicing in Heathcote',
                shortLabel: 'Heathcote',
                href: '#heathcote',
            },
            {
                label: 'Split system servicing in Caringbah',
                shortLabel: 'Caringbah',
                href: '#caringbah',
            },
            {
                label: 'Split system servicing in Kirrawee',
                shortLabel: 'Kirrawee',
                href: '#kirrawee',
            },
            {
                label: 'Split system servicing in Como',
                shortLabel: 'Como',
                href: '#como',
            },
            {
                label: 'Split system servicing in Miranda',
                shortLabel: 'Miranda',
                href: '#miranda',
            },
            {
                label: 'Split system servicing in Loftus',
                shortLabel: 'Loftus',
                href: '#loftus',
            },
            {
                label: 'Split system servicing in Bangor',
                shortLabel: 'Bangor',
                href: '#bangor',
            },
        ],
        footerNote: {
            text: "Can't see your suburb? We likely service it!",
            linkLabel: 'Call 0414 727 244',
            href: 'tel:0414727244',
            suffix: 'to confirm.',
            icon: 'mapPin' as ServiceAreasIconToken,
        },
    },
} as const;

export type ServiceAreasColorToken = keyof typeof serviceAreasTokens.colors;
